import { useRef } from "react"
import LogRocket from "logrocket"
import { UserInfoType } from "../Reducers/types"

const REDACTED = "[REDACTED]"

function redactPasswords(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactPasswords)
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key,
        /password/i.test(key) ? REDACTED : redactPasswords(nested),
      ])
    )
  }
  return value
}

function sanitizeBody(body: string | undefined) {
  if (!body || !/password/i.test(body)) {
    return body
  }
  try {
    return JSON.stringify(redactPasswords(JSON.parse(body)))
  } catch {
    // Non-JSON body mentioning a password: drop it entirely
    return REDACTED
  }
}

export function useLogRocket() {
  const isInit = useRef<boolean>(false)

  const init = () => {
    if (import.meta.env.VITE_LOG_ROCKET_LOG_ID) {

      const isEmbeddedInWebIframe = window.self !== window.top
      
      LogRocket.init(import.meta.env.VITE_LOG_ROCKET_LOG_ID, {
        ...(isEmbeddedInWebIframe
          ? {
              mergeIframes: true,
              parentDomain: import.meta.env.VITE_ICASH_BASE_URL,
            }
          : {}),
        network: {
          requestSanitizer(request) {
            if (request.body) {
              try {
                if (request.url.indexOf('/application/step1') !== -1) {
                  let body = JSON.parse(request.body)
                  body.sin = 'HIDDEN_FOR_LOG_ROCKET'
                  request.body = JSON.stringify(body)
                }
              } catch (error) {}
            }
            return {
              ...request,
              headers: Object.fromEntries(
                Object.entries(request.headers).map(([key, value]) => [
                  key,
                  key.toLowerCase() === "authorization" ? REDACTED : value,
                ])
              ),
              body: sanitizeBody(request.body),
            }
          },
        }
      })
      isInit.current = true
    } else {
      console.warn("REACT_APP_LOG_ROCKET_LOG_ID not set.")
    }
  }

  const identify = (user: UserInfoType | undefined) => {
    if (!isInit.current) {
      init()
    }

    if (isInit.current) {
      LogRocket.identify(user?.contactId?.toString() ?? "", {
        name: `${user?.firstName?.trim()} ${user?.lastName?.trim()}`,
        email: user?.email ?? "",
      })
    }
  }

  return { identify, init }
}
